<template>
  <div
    v-show="shouldShow"
    class="sf-drawer-toggle__container"
    :data-testid="testId"
    @click.stop.prevent="toggleDrawer"
  >
    <slot />
    <i v-if="hasNewItems" class="sf-drawer-toggle__new-indicator"></i>
  </div>
</template>

<script lang="ts" setup>
import { LeftDrawerContent, RightDrawerContent } from '@app/shared/types/drawer'

import { storeToRefs } from 'pinia'
import { computed, onMounted, watch } from 'vue'

import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'
import { useDrawer } from '@app/shared/pinia/drawer'
import getPiniaInstance from '@app/shared/pinia/instance'

interface BaseProps {
  hideIfOpen?: boolean
}

interface LeftDrawerProps extends BaseProps {
  position: 'left'
  content: LeftDrawerContent
}

interface RightDrawerProps extends BaseProps {
  position: 'right'
  content: RightDrawerContent
}

type Props = LeftDrawerProps | RightDrawerProps

const props = defineProps<Props>()

const emits = defineEmits(['shown', 'hidden'])

const hideIfOpen = computed(() => props.hideIfOpen ?? false)

const auditsStore = useAuditsStore(getPiniaInstance())
const drawerStore = useDrawer(getPiniaInstance())
const { open } = storeToRefs(drawerStore)
const { changesSinceLastVisit } = storeToRefs(auditsStore)

const isContextOpen = computed(() => {
  return (
    open.value.content === props.content &&
    open.value.direction === props.position
  )
})

const shouldShow = computed(() => {
  return hideIfOpen.value ? !isContextOpen.value : true
})

const hasNewItems = computed(() => {
  switch (props.content) {
    case RightDrawerContent.AuditLog:
      return changesSinceLastVisit.value.count > 0
    default:
      return false
  }
})

const testId = computed(() => {
  return `sf-drawer-toggle-${props.position}-${props.content}-${
    isContextOpen.value ? 'open' : 'closed'
  }`
})

function toggleDrawer() {
  if (props.position === 'left') {
    drawerStore.toggleDrawer({
      content: props.content,
      direction: 'left',
    })
  } else {
    drawerStore.toggleDrawer({
      content: props.content,
      direction: 'right',
    })
  }
}

onMounted(() => {
  const direction =
    window?.Silverfin?.currentUserPreferences?.open_sidepane?.position
  const content =
    window?.Silverfin?.currentUserPreferences?.open_sidepane?.content

  switch (direction) {
    case 'left':
      if (content === 'default') {
        drawerStore.openDrawerDefault({
          direction,
          content: LeftDrawerContent.Default,
        })
      }
      break
    case 'right':
      switch (content) {
        case 'audit-log':
          drawerStore.openDrawerDefault({
            direction,
            content: RightDrawerContent.AuditLog,
          })
          break
        case 'communication':
          drawerStore.openDrawerDefault({
            direction,
            content: RightDrawerContent.Communication,
          })
          break
        default:
          break
      }
      break
    default:
      break
  }
})

watch(
  shouldShow,
  (newValue, oldValue) => {
    if (!newValue && newValue !== oldValue) {
      emits('hidden')
    }

    if (newValue && newValue !== oldValue) {
      emits('shown')
    }
  },
  {
    immediate: true,
  }
)
</script>
<style lang="scss" scoped>
@import './sf-drawer-toggle';
</style>
